import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import ApiService from '../../../../services/api'
import queryString from 'query-string';
import TableModal from '@/components/TableModal';
import { useMembers, useProspectMembers } from '@/hooks/useApi';
import SavingModal from '@/components/SavingModal';
import TransactionDetailModal from '@/components/TransactionDetailModal';
import { tableColumnsMember,renderActionMember, historyColumns } from './columns'
import { Grid } from 'gridjs-react';
import { _ } from "gridjs-react";

const VoluntarySavings = () => {
  const params = queryString.parse(window.location.search);
  const id = params.id || null
  const navigate = useNavigate();
  const [member, setMember] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenMember, setIsOpenMember] = useState(false);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [savingsTypeId, setSavingsTypeId] = useState(null);

  const { data: membersData } = useMembers();
  const members = membersData?.data || [];

  const { data: prospectMembers } = useProspectMembers();
  const prosMember = prospectMembers?.data || [];

  useEffect(() => {
    if (id) {
      fetchMemberDetail();
      fetchHistorySavings();
      fetchSavingsType(); // Fetch savings type ID for voluntary
    }
    setLoading(false);
  }, [id]);

  const fetchSavingsType = async () => {
    try {
      const response = await ApiService.getSavingsTypes();
      if (response && response.success) {
        // Find voluntary savings type
        const voluntaryType = response.data.find(type => type.type === 'voluntary');
        if (voluntaryType) {
          setSavingsTypeId(voluntaryType.id);
        }
      }
    } catch (error) {
      console.error('Error fetching savings type:', error);
    }
  };
  
  const fetchHistorySavings = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`/members/${id}/savings/transactions/by-type/voluntary`);
      setHistory(response.data || []);
    } catch (error) {
      if (error.status !== 403) {
        console.error('Error fetching history:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to load history'
        });
      }
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetailModal = async (transactionId) => {
    try {
      setLoading(true);
      const response = await ApiService.getTransactionDetail(transactionId);
      setSelectedTransaction(response.data);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Error fetching transaction detail:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load transaction details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId, debitAccountId, creditAccountId) => {
    try {
      const response = await ApiService.approvalTransaction(
        transactionId,
        'approve',
        debitAccountId,
        creditAccountId
      );
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Transaction approved and recorded to journal'
      });
      
      setIsDetailModalOpen(false);
      fetchHistorySavings();
    } catch (error) {
      console.error('Error approving transaction:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to approve transaction'
      });
    }
  };

  const handleCancel = async (transactionId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Transaction will be cancelled",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!'
    });
    
    if (result.isConfirmed) {
      try {
        await ApiService.approvalTransaction(transactionId, 'cancel');
        
        Swal.fire({
          icon: 'success',
          title: 'Cancelled!',
          text: 'Transaction has been cancelled'
        });
        
        setIsDetailModalOpen(false);
        fetchHistorySavings();
      } catch (error) {
        console.error('Error cancelling transaction:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to cancel transaction'
        });
      }
    }
  };

  const fetchMemberDetail = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`/members/${id}`);
      setMember([response.data] || []);
    } catch (error) {
      console.error('Error fetching member detail:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load member details'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      true: 'bg-green-100 text-green-800 border-green-300',
      false: 'bg-red-100 text-red-800 border-red-300',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const handleFormSubmit = async (form) => {
    try {
      const memberId = id;
      
      const formWithMemberId = {
        ...form,
        member_id: Number(memberId),
        payment_method_id: Number(form.payment_method_id),
        amount: Number(form.amount),
        debit_account_id: form.debit_account_id ? Number(form.debit_account_id) : undefined,
        credit_account_id: form.credit_account_id ? Number(form.credit_account_id) : undefined,
      };

      const response = await ApiService.post(`/savings/admin/flexible-payment`, formWithMemberId);
      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Setor simpanan success'
        });
      } else {
        throw new Error(response.message || 'Setor Simpanan Failed!');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error || 'Setor Simpanan Failed!'
      });
    }
  }

  const columnMember = [
    {
      name: 'Kode Anggota',
      data: (row) => row.member_id_number,
    },
    {
      name: 'Nama Anggota',
      data: (row) => row.full_name
    },
    {
      name: 'Jenis Simpanan',
      data: (row) => row.id,
      formatter: (cell) => {
        return _(
          <span>
            Simpanan Sukarela
          </span>
        )
      }
    },
    {
      name: 'Kontak',
      data: (row) => row.phone_number, 
    },
    {
      name: 'Status',
      data: (row) => row,
      formatter: (cell) => {
        return _(
          <span className={`text-gray-900 px-3 whitespace-nowrap py-1 text-sm font-semibold rounded-md border ${getStatusBadge(cell.principal_saving_paid)}`}>{cell.principal_saving_paid ? 'Lunas' : 'Belum Lunas'}</span>
        );
      }
    },
    {
      name: 'Info',
      data: (row) => row,
      formatter: (row) => {
        return _(
          <>
            <div className="flex flex-col w-[30px] gap-3 xl:w-48">
              <button
                className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                onClick={() => navigate(`/admin/members/${row.id}`)}
              >
                Detail
              </button>
              <button 
                onClick={() => setIsModalSaving(!isModalSaving)}
                className="inline-flex whitespace-nowrap items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Setor Simpanan Sukarela
              </button>
            </div>
          </>
        )
      }
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading member details...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link 
              to="/dashboard"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <button
                onClick={() => navigate('/admin/deposit-principal-savings')}
                className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
              >
                Deposit Voluntary Savings
              </button>
            </div>
          </li>
          {member && (
            <li aria-current="page">
                <div className="flex items-center">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 truncate max-w-xs">
                        {member.full_name}
                    </span>
                </div>
            </li>
          )}
        </ol>
      </nav>

      {/* Header */}
      <div className="rounded-lg shadow-sm p-8 mb-6">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-start space-x-6">

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-4 mb-3">
                  <h1 className="text-3xl font-bold text-gray-900 truncate">
                    Setoran Simpanan Sukarela 
                  </h1>
                </div>
                
                <div className="flex items-center mb-4">
                  <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-4 0v1h4v-1" />
                  </svg>
                  <span className="text-lg font-medium text-gray-700">Simpanan Sukarela </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row xl:flex-col gap-3 xl:w-48">
            <button 
              onClick={() => setIsOpenMember(true)}
              className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <i className='mgc_book_line mr-3'></i>
              Data Anggota
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
              {/* Left Section - Member Info */}
              <div className="flex-1">
                  <div className="flex items-start space-x-6">
                  {/* Member Avatar/Photo */}
                    <div className="flex-shrink-0">
                      {member?.id_file_url ? (
                          <img 
                              src={member?.id_file_url}
                              alt={member?.full_name}
                              className="w-20 h-20 rounded-full object-cover border-4 border-gray-100 shadow-sm"
                              onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                              }}
                          />
                          ) : (
                          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-200 shadow-sm">
                              <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                              </svg>
                          </div>
                      )}
                      <div className="w-20 h-20 rounded-full bg-gray-100 items-center justify-center border-4 border-gray-200 shadow-sm" style={{ display: 'none' }}>
                          <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                          </svg>
                      </div>
                    </div>
                    <div className='flex-1'>
                      <Grid
                        data={member}
                        columns={columnMember}
                        pagination={false}
                        search={false}
                        sort={false}
                      />
                    </div>
                  </div>
              </div>
            </div>
        </div>
      </>
      <div className="grid grid-cols-1 gap-8">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
        {/* Table History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">History Simpanan Sukarela</h2>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
              <div className="overflow-x-auto">
                <Grid
                  data={history}
                  columns={historyColumns(handleOpenDetailModal)}
                  pagination={{ enabled: true, limit: 10 }}
                  search={false}
                  sort={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SavingModal
        isOpen={isModalSaving}
        onClose={() => setIsModalSaving(false)}
        onSubmit={handleFormSubmit}
        title="Form Setor Simpanan Sukarela"
        subtitle="Simpanan Sukarela"
        type="voluntary"
        savingsTypeId={savingsTypeId}
      />
      
      <TableModal 
        isOpen={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        columns={tableColumnsMember}
        data={prosMember}
        action={renderActionMember}
        title="Data Calon Anggota"
        navigate={navigate}
      />

      <TableModal 
        isOpen={isOpenMember}
        onClose={() => setIsOpenMember(!isOpenMember)}
        columns={tableColumnsMember}
        data={members}
        action={renderActionMember}
        title="Data Anggota"
        navigate={navigate}
      />

      <TransactionDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        transaction={selectedTransaction}
        onApprove={handleApprove}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default VoluntarySavings;
